import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import {
  Grow,
  Popper,
  Box,
  IconButton,
  TextField,
  Typography,
} from "@mui/material/";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { queryLimit } from "./Dashboard";
import { isNil } from "ramda";
import { isNaN } from "lodash";

interface ViewOptionProps {
  itemsPerChart: number;
  setItemsPerChart: (numberOfItem: number) => void;
}

const classes = {
  selected: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  filterText: {
    fontSize: 13,
    marginBottom: 2,
  },
  filterWrapper: {
    border: "1px solid #ccc",
    borderRadius: 1,
    background: "#fff",
    zIndex: 1,
  },
};

const limitMin = 1;

export default function ViewOption({
  itemsPerChart,
  setItemsPerChart,
}: ViewOptionProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: globalThis.MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleChooseViewOption = (event: ChangeEvent<HTMLInputElement>) => {
    setItemsPerChart(parseInt(event.target.value));
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      if (anchorRef.current) {
        anchorRef.current.focus();
      }
    }

    prevOpen.current = open;
  }, [open]);

  // handle fields
  const fieldLimitError =
    isNaN(Number(itemsPerChart)) ||
    isNil(itemsPerChart) ||
    itemsPerChart < limitMin ||
    itemsPerChart > queryLimit;

  return (
    <Box display="flex">
      <div>
        <IconButton
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-label="more"
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <MoreVertIcon />
        </IconButton>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          placement="bottom-end"
          style={{ zIndex: 1 }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Box sx={classes.filterWrapper}>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box maxWidth={200} padding={2}>
                    <Typography sx={classes.filterText}>
                      Enter number of items (from {limitMin} to {queryLimit})
                      you want to show on chart.
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      label="Number of Items"
                      variant="outlined"
                      size="small"
                      value={itemsPerChart || ""}
                      onChange={handleChooseViewOption}
                      InputProps={{
                        inputProps: { min: limitMin, max: queryLimit },
                      }}
                      error={fieldLimitError}
                      helperText={
                        fieldLimitError
                          ? `Enter a number from ${limitMin} to ${queryLimit}`
                          : ""
                      }
                    />
                  </Box>
                </ClickAwayListener>
              </Box>
            </Grow>
          )}
        </Popper>
      </div>
    </Box>
  );
}
